import { createContext, useContext, useEffect, useState } from 'react'
import type { Lang } from './types'

interface LangCtx {
  lang: Lang
  setLang: (l: Lang) => void
}

const Ctx = createContext<LangCtx>({ lang: 'it', setLang: () => {} })

function initialLang(): Lang {
  if (typeof window === 'undefined') return 'it'
  const url = new URL(window.location.href)
  const q = url.searchParams.get('lang')
  if (q === 'en' || q === 'it') return q
  const saved = localStorage.getItem('ancreus_lang')
  if (saved === 'en' || saved === 'it') return saved
  return navigator.language.toLowerCase().startsWith('en') ? 'en' : 'it'
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('it')

  useEffect(() => {
    setLangState(initialLang())
  }, [])

  const setLang = (l: Lang) => {
    setLangState(l)
    try { localStorage.setItem('ancreus_lang', l) } catch { /* noop */ }
    const url = new URL(window.location.href)
    url.searchParams.set('lang', l)
    window.history.replaceState({}, '', url.toString())
    document.documentElement.lang = l
  }

  return <Ctx.Provider value={{ lang, setLang }}>{children}</Ctx.Provider>
}

export function useLang(): LangCtx {
  return useContext(Ctx)
}

export const L = {
  it: {
    nav_project: 'Il progetto',
    nav_singoli: 'Singoli',
    nav_videos: 'Video',
    nav_live: 'Dal vivo',
    nav_contatti: 'Contatti',
    section_progetto: 'Il progetto',
    section_singoli_eyebrow: 'Singoli · Ancreus Recordings',
    section_singoli_title_a: 'Le riletture',
    section_singoli_title_b: 'in studio',
    section_singoli_intro: "Cinque brani già disponibili. Il lavoro di sala prove registrato e restituito all'ascolto.",
    section_videos_eyebrow: 'Video',
    section_videos_title: 'In presa diretta',
    section_videos_intro: 'Brani ripresi dal vivo e in studio.',
    section_live_eyebrow: 'Dal vivo',
    section_live_title_a: 'Prossime',
    section_live_title_b: 'date',
    section_live_intro: '120 minuti di musica · 25–30 brani.\nDue registri: raccolto / teatrale o essenziale da locale.',
    section_live_propose: 'Proponi una data',
    section_live_empty: 'Nessuna data pubblicata al momento. Scriveteci per proporne una.',
    section_repertorio_eyebrow: 'Repertorio e riferimenti',
    section_repertorio_title_a: 'Autori',
    section_repertorio_title_b: 'in scaletta',
    section_repertorio_intro: 'Il repertorio attraversa autori e mondi diversi, mantenendo sempre una coerenza timbrica ed emotiva.',
    contatti_title_a: 'Scriveteci',
    contatti_title_b: 'senza filtri.',
    contatti_intro: 'Raccontateci il contesto, la data, il luogo. Torneremo a voi con il formato più adatto — teatrale o essenziale — e una proposta di scaletta.',
    label_referente: 'Referente',
    label_email: 'Email',
    label_phone: 'Telefono',
    label_base: 'Base operativa',
    label_social: 'Social',
    form_name: 'Nome e cognome',
    form_email: 'Email',
    form_when: 'Data e luogo',
    form_format_ph: 'Formato · teatrale o essenziale',
    form_format_theatrical: 'Teatrale',
    form_format_essential: 'Essenziale',
    form_format_tbd: 'Da definire',
    form_msg: 'Raccontateci il contesto.',
    form_submit: 'Invia richiesta',
    footer_rights: '© 2025 Ancreus Recordings · Tutti i diritti riservati',
    time_prefix: 'ore',
    format_label: 'Formato',
    info_link: 'Info →',
    single_suffix: 'rilettura Ancreus',
  },
  en: {
    nav_project: 'The project',
    nav_singoli: 'Singles',
    nav_videos: 'Videos',
    nav_live: 'Live',
    nav_contatti: 'Contact',
    section_progetto: 'The project',
    section_singoli_eyebrow: 'Singles · Ancreus Recordings',
    section_singoli_title_a: 'Studio',
    section_singoli_title_b: 're-readings',
    section_singoli_intro: 'Five tracks already available. Rehearsal-room work brought back to listening.',
    section_videos_eyebrow: 'Videos',
    section_videos_title: 'As they happen',
    section_videos_intro: 'Songs captured live and in the studio.',
    section_live_eyebrow: 'Live',
    section_live_title_a: 'Upcoming',
    section_live_title_b: 'dates',
    section_live_intro: '120 minutes of music · 25–30 songs.\nTwo registers: intimate / theatrical, or essential for venues.',
    section_live_propose: 'Propose a date',
    section_live_empty: 'No published dates at the moment. Write to us to propose one.',
    section_repertorio_eyebrow: 'Repertoire and references',
    section_repertorio_title_a: 'Authors',
    section_repertorio_title_b: 'in the setlist',
    section_repertorio_intro: 'The repertoire crosses different authors and worlds, keeping a coherent emotional and timbral thread.',
    contatti_title_a: 'Write to us',
    contatti_title_b: 'without filters.',
    contatti_intro: 'Tell us the context, the date, the place. We will come back with the most suitable format — theatrical or essential — and a setlist proposal.',
    label_referente: 'Contact',
    label_email: 'Email',
    label_phone: 'Phone',
    label_base: 'Based in',
    label_social: 'Social',
    form_name: 'Full name',
    form_email: 'Email',
    form_when: 'Date and place',
    form_format_ph: 'Format · theatrical or essential',
    form_format_theatrical: 'Theatrical',
    form_format_essential: 'Essential',
    form_format_tbd: 'To be defined',
    form_msg: 'Tell us the context.',
    form_submit: 'Send request',
    footer_rights: '© 2025 Ancreus Recordings · All rights reserved',
    time_prefix: 'at',
    format_label: 'Format',
    info_link: 'Info →',
    single_suffix: 'Ancreus re-reading',
  },
} as const

export function t(lang: Lang, key: keyof typeof L.it): string {
  return L[lang][key]
}
